import { Injector } from '@angular/core';
import { AutoUploadEntityEnum } from '../../../AutoUploadEntityEnum';
import { ServiceEnum } from '../../../ServiceEnum';
import { BaseNode } from '../../BaseNode';
import { PharmaceuticalFormEntity } from '../../../Data/MedicinalProductData/PharmaceuticalFormEntity';
import { TreeNodePharmaceuticalFormList } from '../../Art57Node/ListEntity/TreeNodePharmaceuticalFormList';
import { TreeNodeMedicinalProductDrugRoute } from '../ListEntity/TreeNodeMedicinalProductDrugRoute';
import { TreeNodeMedicinalProductPharmaceuticalProducts } from '../ListEntity/TreeNodeMedicinalProductPharmaceuticalProducts';
import { TreeNodeMedicinalProductsMedicinalDevices } from '../ListEntity/TreeNodeMedicinalProductsMedicinalDevices';
import { TreeNodeMedicinalProductsDrugIngredientsList } from '../ListEntity/TreeNodeMedicinalProductsDrugIngredientsList';
import { Art57NodeUtils } from '../../Art57Node/Art57NodeUtils';
import { TreeNodeMedicinalProductsOldDrugIngredientsList } from '../ListEntity/TreeNodeMedicinalProductsOldDrugIngredientsList';
import { TreeNodeMedicinalProductsAbstractPharmaceuticalProduct } from '../ListEntity/TreeNodeMedicinalProductsAbstractPharmaceuticalProduct';
import { EntityEnum } from '../../EntityEnum';
import { environment } from '../../../../../../environments/environment';
import { OnDemandAutoUploadNode } from '../OnDemandAutoUploadNode';

export class PharmaceuticalFormAutoUploadEntity extends OnDemandAutoUploadNode {
  useHist: boolean = false;
  constructor(
    injector: Injector,
    labelTemplate: string,
    parent: BaseNode,
    model: any,
    useHist: boolean,
    disableAutoUpload: boolean,
    cloningNode: boolean
  ) {
    const createChildTemplateparams = { nodeType: 2 };
    const dummyChild =
      parent instanceof TreeNodeMedicinalProductPharmaceuticalProducts &&
      !cloningNode;
    super(
      injector,
      labelTemplate,
      PharmaceuticalFormEntity,
      useHist ? AutoUploadEntityEnum.pharmaceuticalFormHist : AutoUploadEntityEnum.pharmaceuticalForm,
      ServiceEnum.MedicinalProducts,
      parent,
      model,
      TreeNodePharmaceuticalFormList,
      dummyChild,
      disableAutoUpload,
      cloningNode,
      createChildTemplateparams
    );
    this.labelNodeName = 'Pharmaceutical Form';
    this.useHist = useHist;
  }

  override postLoadEntity(model: any): void {

    if (this.parent instanceof TreeNodeMedicinalProductPharmaceuticalProducts) {
      // use model instead of model becasue model is retrieved from autoupload request
      // and we have the information required in this case from the request that create this entity (model)

      // add childrens entities
      this.children?.push(
        new TreeNodeMedicinalProductDrugRoute(
          this.ioc!,
          this,
          false,
          model.adminroutes ? model.adminroutes.adminroute : []
        )
      );
      // Create drug ingredients LIST node
      this.children?.push(
        new TreeNodeMedicinalProductsDrugIngredientsList(this.ioc!, this)
      );
      // import drug ingredients nodes
      if (model.activeingredients) {
        Art57NodeUtils.importNodeType(
          this,
          model.activeingredients,
          'activeingredient',
          TreeNodeMedicinalProductsDrugIngredientsList,
          { roleofingredient: 1 }
        );
      }

      if (model.adjuvants) {
        Art57NodeUtils.importNodeType(
          this,
          model.adjuvants,
          'adjuvant',
          TreeNodeMedicinalProductsDrugIngredientsList,
          { roleofingredient: 3 }
        );
      }

      if (model.excipients) {
        Art57NodeUtils.importNodeType(
          this,
          model.excipients,
          'excipient',
          TreeNodeMedicinalProductsDrugIngredientsList,
          { roleofingredient: 2 }
        );
      }

      this.children?.push(
        new TreeNodeMedicinalProductsOldDrugIngredientsList(this.ioc!, this)
      );
      if (model.olddrugingredients) {
        Art57NodeUtils.importNodeType(
          this,
          model.olddrugingredients,
          'oldDrugIngredient',
          TreeNodeMedicinalProductsOldDrugIngredientsList,
          {}
        );
      }

      this.children?.push(
        new TreeNodeMedicinalProductsMedicinalDevices(
          this.ioc!,
          this,
          false,
          model.medicaldevices ? model.medicaldevices.medicaldevice : []
        )
      );
      this.children?.push(
        new TreeNodeMedicinalProductsAbstractPharmaceuticalProduct(
          this.ioc!,
          model.fk_product,
          model.pharmprodseqnumber,
          EntityEnum.abstractPharmaceuticalProduct,
          this,
          this.useHist,
          false,
          false
        )
      );
      this.children?.push(
        new TreeNodeMedicinalProductsAbstractPharmaceuticalProduct(
          this.ioc!,
          model.fk_product,
          model.pharmprodseqnumber,
          EntityEnum.abstractPharmaceuticalProductSYM,
          this,
          this.useHist,
          false,
          false
        )
      );
    }
  }

  public getListItem(): any[] {
    return [this.data.model.name_Pharmform];
  }

  override cloneNode(newParent: BaseNode): BaseNode|undefined {
    let clonedNode: PharmaceuticalFormAutoUploadEntity =
      new PharmaceuticalFormAutoUploadEntity(
        this.ioc!,
        this.labelTemplate,
        newParent,
        this.data.model,
        this.useHist,
        this.isLoaded,
        true
      );

    clonedNode.disableAutoUpload = true;  // information is previously loaded so we have it in this.datasource
    clonedNode.loadEntity();
    clonedNode.isLoading = false;

    clonedNode.data.model = JSON.parse(JSON.stringify(this.data.model));
    clonedNode.refreshLabel();
    clonedNode.expanded = (this.expanded && environment.userPreferences.autoexpandNodesOnCloning) || environment.userPreferences.alwaysAutoexpandNodes;

    if (!this.children || this.children.length===1) { // check if dummy node
      clonedNode.postLoadEntity(this.datasource);
      clonedNode.cloningNode = false;
      return clonedNode; 
    }
    this.children?.forEach((child) => {
      let childToClone = child as BaseNode;
      let clonedChildren = childToClone.cloneNode(clonedNode);
      if (clonedChildren) {
          clonedNode.children?.push(clonedChildren);
          clonedChildren.refreshLabel();
      }
    });

    clonedNode.cloningNode = false;
    return clonedNode;
  }

    override getOnDemandEntities() {
        if (this.parent instanceof TreeNodeMedicinalProductPharmaceuticalProducts) {
            const onDemandEntities: any = super.getOnDemandEntities();
            if (Array.isArray(onDemandEntities ) || Object.keys(onDemandEntities ).length !== 0) {
                return {id: this.code, ...onDemandEntities};
            }
            return {};
        } else {
            return {};
        }
    }

}
